import { apiFetch } from "../lib/api";
import type { BackfillJob } from "./data";

export type BacktestStatus =
  | "PENDING"
  | "RUNNING"
  | "SUCCESS"
  | "FAILED"
  | "CANCELLED";

export type SizingMode =
  | "fixed_lots"
  | "fixed_rupees"
  | "percent_of_capital"
  | "atr_volatility_target"
  | "risk_per_trade";

export interface BacktestSummary {
  id: string;
  user_id: string;
  strategy_key: string;
  strategy_params: Record<string, unknown>;
  sizing_mode: SizingMode;
  sizing_params: Record<string, unknown>;
  symbol: string;
  exchange: string;
  instrument_type: string;
  timeframe: string;
  start_ts: string;
  end_ts: string;
  initial_capital: string;
  margin: string;
  status: BacktestStatus;
  bars_processed: number | null;
  fills_count: number | null;
  net_pnl: string | null;
  return_pct: string | null;
  max_drawdown_pct: string | null;
  sharpe: string | null;
  win_rate_pct: string | null;
  closing_trades: number | null;
  final_equity: string | null;
  error_message: string | null;
  notes: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BacktestTrade {
  idx: number;
  ts: string;
  side: string;
  symbol: string;
  quantity: string;
  price: string;
  fees: string;
  realized_pnl_delta: string;
  tag: string | null;
}

export interface BacktestDetail extends BacktestSummary {
  slippage_bps: number;
  /** Whether this run's P&L is net of brokerage and statutory charges. A net
   *  result is not comparable to a gross one, so it travels with the run. */
  include_charges?: boolean;
  charges_broker?: string | null;
  latency_ms: number;
  expiry: string | null;
  strike: string | null;
  lot_size: number;
  tick_size: string;
  orders_submitted: number | null;
  orders_rejected: number | null;
  /** How many trades the run produced. The trades themselves come from
   *  `listBacktestTrades` — see the note there for why they no longer ride
   *  along with the detail. */
  trades_count: number;
  /** Per-session golden numbers (day = IST date "YYYY-MM-DD"). Populated for
   *  the Golden Number family in any injected (non-manual) mode; empty
   *  otherwise (manual mode is drawn client-side from the param). */
  golden_numbers: GoldenNumber[];
  /** Per-day value area. Only populated for runs that use it. */
  value_area: ValueArea[];
  /** Regime-sliced metrics (by year / trend / volatility). Null for runs
   *  that predate the feature. */
  regime_report: RegimeReport | null;
}

export interface RegimeSliceRow {
  label: string;
  n_bars: number;
  time_pct: number;
  net_pnl: number;
  return_pct: number;
  sharpe: number | null;
  max_drawdown_pct: number;
  closing_trades: number;
  win_rate_pct: number | null;
}

export interface RegimeReport {
  trend_window_days: number;
  vol_window_days: number;
  by_year: RegimeSliceRow[];
  by_trend: RegimeSliceRow[];
  by_vol: RegimeSliceRow[];
}

export interface GoldenNumber {
  day: string;
  value: string;
}

export interface ValueArea {
  day: string;
  vah: string;
  val: string;
}

export interface BacktestCreate {
  strategy_key: string;
  strategy_params: Record<string, unknown>;
  sizing_mode: SizingMode;
  sizing_params: Record<string, unknown>;
  symbol: string;
  exchange: string;
  instrument_type?: string;
  expiry?: string | null;
  strike?: string | null;
  lot_size?: number;
  tick_size?: string;
  timeframe: string;
  start: string;
  end: string;
  initial_capital?: string;
  margin?: string;
  slippage_bps?: number;
  include_charges?: boolean;
  charges_broker?: string;
  charges_product?: string;
  latency_ms?: number;
}

export interface BacktestListQuery {
  limit?: number;
  offset?: number;
  /** Space-separated tokens, AND-ed, matched against the run id (substring,
   *  so an 8-char prefix or a pasted UUID both work), strategy, symbol,
   *  exchange, instrument type, timeframe, status and the run's note. */
  q?: string;
  status?: BacktestStatus | "";
}

function listParams({ limit = 25, offset = 0, q, status }: BacktestListQuery): string {
  const p = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (q && q.trim()) p.set("q", q.trim());
  if (status) p.set("status", status);
  return p.toString();
}

export async function listBacktests(
  query: BacktestListQuery = {},
): Promise<BacktestSummary[]> {
  return apiFetch<BacktestSummary[]>(`/backtests?${listParams(query)}`);
}

/** Total runs matching the same filters — what the pager needs to know
 *  whether an older page exists. `limit`/`offset` are ignored server-side. */
export async function countBacktests(
  query: Omit<BacktestListQuery, "limit" | "offset"> = {},
): Promise<number> {
  const { total } = await apiFetch<{ total: number }>(
    `/backtests/count?${listParams(query)}`,
  );
  return total;
}

export async function getBacktest(id: string): Promise<BacktestDetail> {
  return apiFetch<BacktestDetail>(`/backtests/${id}`);
}

export interface BacktestTradePage {
  items: BacktestTrade[];
  total: number;
}

/** The cap the server puts on one page. The chart markers and the P&L
 *  calendar need every trade and ask for this; the biggest run in the store
 *  has ~42k, so it is set above that rather than truncating them. */
export const MAX_TRADES_PER_PAGE = 50_000;

/** One page of a run's trades, in engine order.
 *
 *  Trades used to arrive inside `getBacktest`, alongside the run's full equity
 *  series. Measured on a typical 1m `daily_htp_ladders` run that detail
 *  response was 14 MB — 13.5 MB of it a 153,221-point equity series that no
 *  component has ever rendered — and took over a second server-side before the
 *  browser had parsed a byte. Now the detail is small and paints at once, the
 *  table pages 25 at a time, and the chart/calendar fetch their copy after.
 */
export async function listBacktestTrades(
  id: string,
  { limit = 25, offset = 0 }: { limit?: number; offset?: number } = {},
): Promise<BacktestTradePage> {
  return apiFetch<BacktestTradePage>(
    `/backtests/${id}/trades?limit=${limit}&offset=${offset}`,
  );
}

export async function createBacktest(
  payload: BacktestCreate,
): Promise<BacktestSummary> {
  return apiFetch<BacktestSummary>("/backtests", {
    method: "POST",
    body: payload,
  });
}

export async function cancelBacktest(id: string): Promise<BacktestSummary> {
  return apiFetch<BacktestSummary>(`/backtests/${id}/cancel`, {
    method: "POST",
  });
}

/** Save the run's free-text note (inline edit in the backtests list). */
export async function updateBacktestNotes(
  id: string,
  notes: string,
): Promise<BacktestSummary> {
  return apiFetch<BacktestSummary>(`/backtests/${id}/notes`, {
    method: "PATCH",
    body: { notes },
  });
}

/** Enqueue a data backfill for a failed run's instrument/timeframe/window. */
export async function backfillForBacktest(id: string): Promise<BackfillJob> {
  return apiFetch<BackfillJob>(`/backtests/${id}/backfill`, { method: "POST" });
}

/** Re-run a backtest with the same config (after its data is backfilled). */
export async function rerunBacktest(id: string): Promise<BacktestSummary> {
  return apiFetch<BacktestSummary>(`/backtests/${id}/rerun`, { method: "POST" });
}
